import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { BookmarkIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getStaffApplication } from '../../api/staff-application/get-staff-application'
import { updateStaffApplication } from '../../api/staff-application/update-staff-application'
import DateChip from '../../components/elements/date-chip'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import StaffApplicationForm from '../../components/pages/staff-application/staff-application-form'
import { mapStaffApplicationToInput } from '../../components/pages/staff-application/utils'
import { Paths } from '../../lib/constants/path'
import useAuth from '../../lib/hooks/use-auth'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import { StaffApplicationInput } from '../../lib/types/models/staff-application/staff-application-input'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

type Props = {
  readOnly?: boolean
}

const UpdateStaffApplicationPage: React.FC<Props> = ({ readOnly }) => {
  const id = useParams<{ id: string }>().id
  const { back } = useNavigator()
  const { userRole } = useAuth()

  const returnPath =
    userRole === RoleEnum.PCR ? Paths.PCR_PERSONAL_SPACE_KANBAN : Paths.STAFF_APPLICATIONS_PAGE

  const {
    data: application,
    isPending,
    refetch
  } = useQuery({
    queryKey: ['staff-application', id],
    queryFn: () => {
      if (!id || isNaN(+id)) return
      return getStaffApplication(+id)
    }
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateStaffApplication,
    onSuccess: () => {
      refetch()
      toast.success('Candidature modifiée avec succès')
    },
    onError: showAxiosError
  })

  const handleSubmit = async (values: StaffApplicationInput) => {
    if (!id || isNaN(+id)) return
    await mutateAsync({ id: +id, ...values })
  }

  if (isPending) return <LoadingScreen />
  if (!application || !id) return <NotFoundPage />

  return (
    <>
      <UserNavBar
        title="Candidature encadrant"
        icon={(props) => BookmarkIcon({ ...props, className: 'h-6 w-6' })}
      />
      <Box className="mt-5">
        <div className="flex items-center gap-3 mb-4">
          <DateChip date={new Date(application.createdAt)} label="Créé" />
          <DateChip date={new Date(application.updatedAt)} label="Mise à jour" />
        </div>

        <StaffApplicationForm
          staffApplicationId={+id}
          readOnly={readOnly}
          defaultValues={mapStaffApplicationToInput(application)}
          buttonsConfig={updateButtonsConfig({
            onUpdate: readOnly ? undefined : handleSubmit,
            onCancel: () => back(returnPath),
            onUpdateThenList: readOnly
              ? undefined
              : async (values) => {
                  await handleSubmit(values)
                  back(returnPath)
                }
          })}
        />
      </Box>
    </>
  )
}

export default UpdateStaffApplicationPage
